import {
  PanelSection,
  Table,
  type TableMiniColumn,
} from '@dev-spendesk/grapes';
import { useFormik } from 'formik';

import { useCompany } from 'modules/app/hooks/useCompany';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import {
  type BankDetailsFormValues,
  type SupplierDetails,
} from 'modules/bookkeep/accounts-payable/types';
import { CountryFlag } from 'src/core/common/components/CountryFlag/CountryFlag';
import { EllipsisTooltip } from 'src/core/common/components/EllipsisTooltip';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { COUNTRIES } from 'src/core/config/country';
import { BankFields } from 'src/core/utils/bankInfoFormats';
import { useBankInfoFieldsCombo } from 'src/core/utils/useBankInfo';

import {
  AccountPayableSuppliersBankFields,
  validateBankDetailsForm,
} from '../../../components/AccountPayableSuppliersBankFields';
import { useUpdateSupplierDetailsMutation } from '../../../hooks/useUpdateSupplierDetailsMutation';

type Props = {
  supplier: SupplierDetails;
};

export const AccountPayableSuppliersPanelBankDetailsSection = ({
  supplier,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
Props) => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const { pushNotif } = useNotifications();

  const [updateSupplierDetailsMutation] = useUpdateSupplierDetailsMutation();

  const getBankInfoFields = useBankInfoFieldsCombo();
  const bankInfoFields = getBankInfoFields(
    supplier.bankDetails?.bankCountry || null,
  );

  const initialValues = {
    bankCountry: supplier.bankDetails?.bankCountry ?? '',
    iban: supplier.bankDetails?.iban ?? '',
    bicSwift: supplier.bankDetails?.bicSwift ?? '',
    sortCode: supplier.bankDetails?.sortCode ?? '',
    routingNumber: supplier.bankDetails?.routingNumber ?? '',
    accountNumber: supplier.bankDetails?.accountNumber ?? '',
    accountCode: supplier.bankDetails?.accountCode ?? '',
    accountHolderName: supplier.accountHolderName ?? '',
  };

  const formikProps = useFormik<BankDetailsFormValues>({
    enableReinitialize: true,
    initialValues,
    validateOnChange: false,
    validate: (values: BankDetailsFormValues) => {
      return validateBankDetailsForm(company, values);
    },
    // eslint-disable-next-line sonarjs/cognitive-complexity
    onSubmit: async (values: BankDetailsFormValues) => {
      const submittedBankInfoFields = getBankInfoFields(
        values.bankCountry || null,
      );

      const updateSupplierDetailsMutationResult =
        await updateSupplierDetailsMutation({
          supplierId: supplier.id,
          accountHolderName:
            (submittedBankInfoFields.includes(BankFields.AccountHolderName) &&
              values.accountHolderName) ||
            '',
          bankDetails: {
            bankCountry: values.bankCountry,
            iban:
              (submittedBankInfoFields.includes(BankFields.Iban) &&
                values.iban) ||
              '',
            bicSwift:
              (submittedBankInfoFields.includes(BankFields.BicSwift) &&
                values.bicSwift) ||
              '',
            sortCode:
              (submittedBankInfoFields.includes(BankFields.SortCode) &&
                values.sortCode) ||
              '',
            routingNumber:
              (submittedBankInfoFields.includes(BankFields.RoutingNumber) &&
                values.routingNumber) ||
              '',
            accountNumber:
              (submittedBankInfoFields.includes(BankFields.AccountNumber) &&
                values.accountNumber) ||
              '',
            accountCode:
              (submittedBankInfoFields.includes(BankFields.AccountCode) &&
                values.accountCode) ||
              '',
          },
          legalDetails: supplier.legalDetails,
        });

      if (updateSupplierDetailsMutationResult.updateSupplierDetails.reason) {
        pushNotif({
          type: NotificationType.Danger,
          message: t(
            'bookkeep.accountsPayable.panel.bankDetailsSection.updateErrorNotification',
          ),
        });
      } else {
        pushNotif({
          type: NotificationType.Success,
          message: t(
            'bookkeep.accountsPayable.panel.bankDetailsSection.updateSuccessNotification',
          ),
        });
      }
    },
  });

  const data = [
    ...(bankInfoFields.includes(BankFields.AccountHolderName)
      ? [
          {
            id: 'accountHolderName',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.accountHolderNameLabel',
            ),
            value: supplier.accountHolderName || '-',
          },
        ]
      : []),
    {
      id: 'country',
      label: t(
        'suppliers.spanishDprCompliance.missingSupplierDetailsCallout.country.label',
      ),
      value: (
        <span className="flex items-center gap-8">
          {supplier.bankDetails?.bankCountry ? (
            <>
              <CountryFlag
                country={supplier.bankDetails?.bankCountry}
                className="h-[18px] w-[18px]"
              />
              {t(COUNTRIES[supplier.bankDetails?.bankCountry]?.translationKey)}
            </>
          ) : (
            '-'
          )}
        </span>
      ),
    },
    ...(bankInfoFields.includes(BankFields.AccountNumber)
      ? [
          {
            id: 'accountNumber',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.accountNumberLabel',
            ),
            value: supplier.bankDetails?.accountNumber || '-',
          },
        ]
      : []),
    ...(bankInfoFields.includes(BankFields.AccountCode)
      ? [
          {
            id: 'accountCode',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.accountCodeLabel',
            ),
            value: supplier.bankDetails?.accountCode || '-',
          },
        ]
      : []),
    ...(bankInfoFields.includes(BankFields.Iban)
      ? [
          {
            id: 'iban',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.ibanLabel',
            ),
            value: supplier.bankDetails?.iban || '-',
          },
        ]
      : []),
    ...(bankInfoFields.includes(BankFields.BicSwift)
      ? [
          {
            id: 'bicSwift',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.bicSwiftLabel',
            ),
            value: supplier.bankDetails?.bicSwift || '-',
          },
        ]
      : []),
    ...(bankInfoFields.includes(BankFields.SortCode)
      ? [
          {
            id: 'sortCode',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.sortCodeLabel',
            ),
            value: supplier.bankDetails?.sortCode || '-',
          },
        ]
      : []),
    ...(bankInfoFields.includes(BankFields.RoutingNumber)
      ? [
          {
            id: 'routingNumber',
            label: t(
              'bookkeep.accountsPayable.panel.bankDetailsSection.routingNumberLabel',
            ),
            value: supplier.bankDetails?.routingNumber || '-',
          },
        ]
      : []),
  ];

  const columns: TableMiniColumn<(typeof data)[number]>[] = [
    {
      id: 'label',
      renderCell: (cell) => (
        <span className="block w-[120px] text-content-secondary-bg-primary">
          <EllipsisTooltip text={cell.label} />
        </span>
      ),
    },
    {
      id: 'value',
      renderCell: (cell) => (
        <span className="block w-[260px]" data-mask>
          {cell.value}
        </span>
      ),
    },
  ];

  return (
    <PanelSection
      data-testid="bank-details-section"
      title={t('bookkeep.accountsPayable.panel.bankDetailsSection.title')}
      isEditable
      cancelTranslation={t('misc.cancel')}
      saveTranslation={t('misc.saveChanges')}
      editSection={
        <form className="AccountPayableSuppliersPanelBankDetailsForm">
          <AccountPayableSuppliersBankFields {...formikProps} />
        </form>
      }
      onCancel={() => {
        formikProps.resetForm();
      }}
      onSave={async () => {
        // this will trigger 2 form validations
        // it is due to a formik bug that doesn't reject the promise when calling submitForm and there are validation errors
        // https://github.com/formium/formik/issues/1580
        const { submitForm, validateForm } = formikProps;
        const errors = await validateForm();
        const isValid = Object.keys(errors).length === 0;
        if (!isValid) {
          throw new Error('Validation error');
        }
        return submitForm();
      }}
    >
      <Table.Mini
        className="my-8 bg-background-primary-default"
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
      />
    </PanelSection>
  );
};
